<template>
    <div class="space-y-2">
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <div
                v-for="item in items"
                :key="item.key"
                class="space-y-1 rounded-lg bg-dark-light/60 p-3"
                :style="{ borderLeft: `4px solid ${item.color}` }"
            >
                <div class="flex items-center justify-between text-xs tracking-widest text-white uppercase">
                    <span>{{ item.label }}</span>
                    <div class="flex items-center space-x-2">
                        <button
                            type="button"
                            class="rounded bg-dark-lighter px-2 py-1 text-xxs text-white"
                            @click="() => nudgeWithStep(item.key, -1)"
                            :disabled="isLocked || values[item.key] <= 0"
                        >
                            -
                        </button>
                        <span class="text-text-base font-semibold">{{ formatValue(values[item.key]) }}</span>
                        <button
                            type="button"
                            class="rounded bg-dark-lighter px-2 py-1 text-xxs text-white"
                            @click="() => nudgeWithStep(item.key, 1)"
                            :disabled="isLocked || remaining === 0"
                        >
                            +
                        </button>
                    </div>
                </div>
                <input
                    type="range"
                    :min="0"
                    :max="values[item.key] + remaining"
                    :step="stepValue"
                    :value="values[item.key]"
                    @touchstart.stop
                    @touchmove.stop
                    @input="(e) => onSlider(item.key, Number(e.target.value))"
                    :disabled="isLocked"
                    class="w-full accent-brand-base"
                    :style="{ touchAction: 'none' }"
                />
                <div v-if="item.key !== 'standing_distance'" class="text-xxs text-text-darker">
                    {{ formatSpeed(item.key) }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, watch } from 'vue'
import tailwind from '@/lib/tailwind'
import { convertSpeed, convertDistance } from '@/lib/helpers/convert'
import { useGaitAllocation } from './useGaitAllocation'

const props = defineProps({
    totalMeters: { type: Number, default: 0 },
    modelValue: {
        type: Object,
        default: () => ({ walk_distance: 0, trot_distance: 0, canter_distance: 0, standing_distance: 0 }),
    },
    gaitTimes: {
        type: Object,
        default: () => ({ walk_time: 0, trot_time: 0, canter_time: 0, standing_time: 0 }),
    },
    userUnit: { type: String, default: 'm' }, // expected 'm', 'km', 'ft', 'mi', etc.
    userSpeedUnit: { type: String, default: 'm/min' },
})
const emits = defineEmits(['update:modelValue'])

const items = [
    { key: 'standing_distance', label: 'Standing', color: tailwind.theme.color.gaits.standing },
    { key: 'walk_distance', label: 'Walk', color: tailwind.theme.color.gaits.walk },
    { key: 'trot_distance', label: 'Trot', color: tailwind.theme.color.gaits.trot },
    { key: 'canter_distance', label: 'Canter', color: tailwind.theme.color.gaits.canter },
]

const totalRef = computed(() => props.totalMeters)
const displayUnit = computed(() => (props.userUnit === 'mi' || props.userUnit === 'ft' ? 'ft' : 'm'))
const stepValue = computed(() => (displayUnit.value === 'ft' ? 0.3048 : 1)) // step in meters

const emitCurrent = (vals) => {
    emits('update:modelValue', {
        walk_distance: Math.round(vals.walk_distance),
        trot_distance: Math.round(vals.trot_distance),
        canter_distance: Math.round(vals.canter_distance),
        standing_distance: Math.round(vals.standing_distance),
    })
}

const { values, totalSafe, isLocked, remaining, onSlider, nudge, enforceLimits } = useGaitAllocation(
    items.map((i) => i.key),
    totalRef,
    emitCurrent
)

const formatSpeed = (distanceKey) => {
    const timeKey = distanceKey.replace('_distance', '_time')
    const duration = props.gaitTimes?.[timeKey] || 0
    const distance = values[distanceKey] || 0
    if (!duration) return ''
    const speedMPerMin = (distance / duration) * 60
    return convertSpeed(speedMPerMin, 'm/min', props.userSpeedUnit, 1, true)
}

const formatValue = (meters) => {
    return convertDistance(meters || 0, 'm', displayUnit.value, true, 0, true)
}

watch(
    () => props.modelValue,
    (val) => {
        values.walk_distance = Math.max(0, val?.walk_distance ?? 0)
        values.trot_distance = Math.max(0, val?.trot_distance ?? 0)
        values.canter_distance = Math.max(0, val?.canter_distance ?? 0)
        values.standing_distance = Math.max(0, val?.standing_distance ?? 0)
        enforceLimits()
    },
    { immediate: true, deep: true }
)

const nudgeWithStep = (key, delta) => {
    const desired = values[key] + delta * stepValue.value
    onSlider(key, desired)
}

enforceLimits()
emitCurrent(values)
</script>
